import { toast } from "@gigpro/ui/Toast";
import {
  getHireJobCreditsStatsRetrieveQueryKey,
  getHireJobsApplicantsRetrieveQueryKey,
  getHireJobsListInfiniteQueryKey,
  getHireJobsListQueryKey,
  getHireJobsProMatchStatusListQueryKey,
  getHireJobsRetrieveQueryKey,
  getHireJobsSkillsListQueryKey,
  getHireScreeningInterviewsRetrieveQueryKey,
  hireJobCreditsCreate,
  hireJobsMatchesCreate,
  type JobCreditStats,
  type JobPostingResponse,
  type ProMatchStatusResult,
  useHireJobCreditsStatsRetrieve,
  useHireJobsPublishCreate,
  useHireJobOffersCreateWithTransform,
  useHireJobOffersDestroy,
  useHireJobOffersList,
  useHireJobsApplicantsInvitesCreate,
  useHireJobsApplicantsInviteResendCreate,
  useHireJobsApplicantsPartialUpdate,
  useHireJobsApplicantsRetrieveWithTransform,
  useHireJobsApplicantsTimelineRetrieveWithTransform,
  useHireJobsApplicantsWorkingInterviewsCreate,
  useHireJobsCreate,
  useHireJobsDestroy,
  useHireJobsProMatchStatusList,
  useHireJobsRetrieve,
  useHireJobsUpdate,
  useHireInterviewsFeedbackPartialUpdate,
  useHireScreeningInterviewsCancellationsCreate,
  useHireScreeningInterviewsCreateWithTransform,
  useHireScreeningInterviewsFeedbackPartialUpdate,
  useHireScreeningInterviewsPartialUpdateWithTransform,
  type NormalizedInterview,
  useLocationManagersList,
  type StafferJobOffer as JobOffer,
  getHireManagerAvailabilityJobsWeekRetrieveQueryKey,
  getHireManagerAvailabilityWeekListQueryKey,
  useHireManagerAvailabilityJobsWeekDestroy,
  useHireManagerAvailabilityJobsWeekRetrieve,
  useHireManagerAvailabilityJobsWeekUpdate,
  type ManagerAvailabilityWindow,
  type ManagerAvailabilityWindowRequest,
  getHireManagerAvailabilityWeekSummaryRetrieveQueryKey,
  useHireManagerAvailabilityWeekList,
  useHireManagerAvailabilityWeekPreviewList,
  useHireManagerAvailabilityWeekSummaryRetrieve,
  useHireManagerAvailabilityWeekUpdate,
  type PatchedScreeningInterviewFeedbackRequest,
} from "@sidegigllc/api/client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { JobPost } from "api/resources/hire";
import type { AvailabilityWindow } from "domains/hire/components/AvailabilityWindowsField";
import { canScheduleWorkingInterview, combineTimelineEvents, type TimelineEvent } from "domains/hire/utils";
import { times } from "remeda";
import { captureException } from "utils/errorReporting";

type Options = { enabled?: boolean };

const JOB_DETAIL_PATH = /\/hire\/jobs\/[^/]+\/$/;

export const usePrefillApplicantCache = (jobPostingId: string) => {
  const queryClient = useQueryClient();

  // Prefill cache with applicants from list query
  return <T extends { results: Array<{ id: string }> }>(applicantsPage: T): T => {
    for (const applicant of applicantsPage.results) {
      const queryKey = getHireJobsApplicantsRetrieveQueryKey(jobPostingId, applicant.id);
      // Seed only: the list row lacks screening.questions, so overwriting would wipe loaded answers.
      if (!queryClient.getQueryData(queryKey)) queryClient.setQueryData(queryKey, applicant);
    }
    return applicantsPage;
  };
};

export const usePrefillJobCache = () => {
  const queryClient = useQueryClient();

  // Prefill cache with jobs from list query for faster navigation
  return <T extends { results: Array<{ id: string }> }>(jobsPage: T): T => {
    for (const job of jobsPage.results) {
      queryClient.setQueryData(getHireJobsRetrieveQueryKey(job.id), job);
    }
    return jobsPage;
  };
};

const invalidateAfterJobChange = (queryClient: ReturnType<typeof useQueryClient>) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: getHireJobsListInfiniteQueryKey() }),
    // Finite page_size:1 counts query (useJobStatusCounts) drives the tab badges.
    queryClient.invalidateQueries({ queryKey: getHireJobsListQueryKey() }),
    queryClient.invalidateQueries({ queryKey: getHireJobsSkillsListQueryKey() }),
    // The Settings footer counts open postings, so posting, archiving or handing one over moves it.
    // Without this the QueryClient's 10s freshness window serves the pre-mutation counts to a manager
    // who posts a job and goes straight back to Settings — the actions that change the numbers are
    // exactly the ones that would leave them stale.
    queryClient.invalidateQueries({ queryKey: getHireManagerAvailabilityWeekSummaryRetrieveQueryKey() }),
  ]);

export const invalidateAfterApplicantChange = (queryClient: ReturnType<typeof useQueryClient>) =>
  Promise.all([
    // Applicant keys are nested under the job (`/hire/jobs/:id/applicants/…`), so one path match covers
    // the list, funnel counts, retrieves and timelines across every posting.
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.some((key) => typeof key === "string" && key.includes("/applicants/")),
    }),
    queryClient.invalidateQueries({ queryKey: getHireJobsListInfiniteQueryKey() }),
    // Job-detail stats (job.stats) drive the stage tabs, so invalidate the retrieve queries by path.
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.some((key) => typeof key === "string" && JOB_DETAIL_PATH.test(key)),
    }),
  ]);

export const useJob = (jobId: string, options?: Options) => {
  const queryClient = useQueryClient();
  const query = useHireJobsRetrieve(jobId, { query: { enabled: options?.enabled } });
  return {
    ...query,
    invalidate: () => queryClient.invalidateQueries({ queryKey: getHireJobsRetrieveQueryKey(jobId) }),
  };
};

export const useApplicant = (jobPostingId: string, applicantId: string | undefined) =>
  useHireJobsApplicantsRetrieveWithTransform(jobPostingId, applicantId ?? "", {
    query: {
      enabled: !!applicantId,
      staleTime: 1000 * 60 * 5, // 5 min stale time - cache populated by list query
    },
  });

export const useApplicantDetail = (jobPostingId: string, applicantId: string | undefined, enabled = true) =>
  useHireJobsApplicantsRetrieveWithTransform(jobPostingId, applicantId ?? "", {
    query: { enabled: enabled && !!applicantId, staleTime: 0 },
  });

// Active managers of a location, shaped for a select: `{ id: userId, name }`. Reuses the
// location-managers list endpoint (active managers only by default) so the job-posting form's
// Hiring Manager dropdown has no bespoke backend. `id` is the User id (sent as hiring_manager_id).
export const useActiveLocationManagers = (locationId: number) =>
  useLocationManagersList(
    { location: locationId },
    {
      query: {
        enabled: !!locationId,
        select: (data) =>
          data
            .filter((m) => m.status === "active")
            .map((m) => ({
              id: m.user.id,
              name: (m.user.name || `${m.user.first_name} ${m.user.last_name}`).trim(),
            })),
      },
    },
  );

// Job mutations with auto-invalidation. The wrappers re-expose `mutate`/`mutateAsync`
// taking the bare `JobPost` form body (a subset of the generated wire request).
export const usePostJob = (options?: { onSuccess?: (job: JobPostingResponse) => void | Promise<void> }) => {
  const queryClient = useQueryClient();
  const mutation = useHireJobsCreate({
    mutation: {
      onSuccess: async (job) => {
        await invalidateAfterJobChange(queryClient);
        // Awaited so a caller can persist a follow-up resource keyed by the new job id before it
        // tears down the form.
        await options?.onSuccess?.(job);
      },
    },
  });
  return {
    ...mutation,
    mutate: (data: JobPost) => mutation.mutate({ data }),
    mutateAsync: (data: JobPost) => mutation.mutateAsync({ data }),
  };
};

export const useUpdateJob = (
  jobId: string,
  options?: { onSuccess?: (job: JobPostingResponse) => void | Promise<void> },
) => {
  const queryClient = useQueryClient();
  const mutation = useHireJobsUpdate({
    mutation: {
      onSuccess: async (job) => {
        await invalidateAfterJobChange(queryClient);
        await options?.onSuccess?.(job);
      },
    },
  });
  return {
    ...mutation,
    mutate: (data: JobPost) => mutation.mutate({ id: jobId, data }),
    mutateAsync: (data: JobPost) => mutation.mutateAsync({ id: jobId, data }),
  };
};

export const useDeleteJob = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  return useHireJobsDestroy({
    mutation: {
      onSuccess: async () => {
        await invalidateAfterJobChange(queryClient);
        options?.onSuccess?.();
      },
    },
  });
};

/** Moves a private or closed direct-hire job to a normal open one. */
export const usePublishJob = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();
  return useHireJobsPublishCreate({
    mutation: {
      onSuccess: async (_job, { id }) => {
        await Promise.all([
          invalidateAfterJobChange(queryClient),
          queryClient.invalidateQueries({ queryKey: getHireJobsRetrieveQueryKey(id) }),
        ]);
        options?.onSuccess?.();
      },
    },
  });
};

export const useDismissApplicant = () => {
  const queryClient = useQueryClient();

  return useHireJobsApplicantsPartialUpdate({
    mutation: { onSuccess: () => invalidateAfterApplicantChange(queryClient) },
  });
};

export const useCreateWorkingInterview = (
  jobPostingId: string,
  applicantId?: string,
  options?: { onSuccess?: () => void },
) => {
  const queryClient = useQueryClient();
  const mutation = useHireJobsApplicantsWorkingInterviewsCreate({
    mutation: {
      onSuccess: async () => {
        await invalidateAfterApplicantChange(queryClient);
        options?.onSuccess?.();
      },
    },
  });
  const variables = (data: { gigs: Array<number> }) => ({ jobPostingId, id: applicantId ?? "", data });
  return {
    ...mutation,
    mutate: (data: { gigs: Array<number> }) => mutation.mutate(variables(data)),
    mutateAsync: (data: { gigs: Array<number> }) => mutation.mutateAsync(variables(data)),
  };
};

export const useApplicantTimeline = (
  jobPostingId: string,
  applicantId: string | undefined,
  timezone: string | null | undefined,
  options?: Options,
) =>
  useHireJobsApplicantsTimelineRetrieveWithTransform<Array<TimelineEvent>>(jobPostingId, applicantId ?? "", timezone, {
    query: {
      enabled: !!applicantId && options?.enabled !== false,
      select: (data) =>
        combineTimelineEvents(data.interviews, data.screening_interviews, data.ai_screenings, data.job_offers),
    },
  });

const invalidateAfterJobInvite = (queryClient: ReturnType<typeof useQueryClient>) =>
  Promise.all([
    // Match keys embed the job id in their URL, so one path match covers every posting.
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.some((key) => typeof key === "string" && key.includes("/matches/")),
    }),
    queryClient.invalidateQueries({ queryKey: getHireJobsProMatchStatusListQueryKey() }),
  ]);

export const useInviteProToJob = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobIds, proId }: { jobIds: Array<string>; proId: number }) => {
      await Promise.all(jobIds.map((jobId) => hireJobsMatchesCreate(jobId, { pro_id: proId, status: "INVITED" })));
    },
    onSuccess: async () => {
      await invalidateAfterJobInvite(queryClient);
      options?.onSuccess?.();
    },
  });
};

/** Invites many pros to one job; each invite settles on its own, so one failure doesn't drop the rest. */
export const useInviteProsToJob = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, proIds }: { jobId: string; proIds: Array<number> }) => {
      const results = await Promise.allSettled(
        proIds.map((proId) => hireJobsMatchesCreate(jobId, { pro_id: proId, status: "INVITED" })),
      );
      const rejected = results.filter((result) => result.status === "rejected");
      for (const { reason } of rejected) captureException(reason);
      return { failed: rejected.length };
    },
    onSuccess: () => invalidateAfterJobInvite(queryClient),
  });
};

export const useApplicantInvites = () => {
  const queryClient = useQueryClient();

  return useHireJobsApplicantsInvitesCreate({
    mutation: {
      onSuccess: async (_data, { params }) => {
        if (params?.save === false) return;
        await Promise.all([invalidateAfterApplicantChange(queryClient), invalidateAfterJobInvite(queryClient)]);
      },
    },
  });
};

export const useResendApplicantInvite = () => {
  const queryClient = useQueryClient();

  return useHireJobsApplicantsInviteResendCreate({
    mutation: { onSuccess: () => invalidateAfterApplicantChange(queryClient) },
  });
};

export const useConfirmApplicant = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ jobId, ...target }: { jobId: string; pro_id?: number; group_id?: number }) => {
      try {
        const result = await hireJobsMatchesCreate(jobId, { ...target, status: "INVITED", confirm_applicant: true });
        const matches = Array.isArray(result) ? result : [result];
        return matches.map((match) => match.candidate_id).filter(Boolean);
      } catch (error) {
        captureException(error);
        toast.error("Couldn't schedule interview. Please try again.");
        return [];
      }
    },
    onSuccess: async (candidateIds) => {
      if (!candidateIds.length) return;
      await invalidateAfterApplicantChange(queryClient);
      options?.onSuccess?.();
    },
  });
};

export const useCreateJobOffer = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useHireJobOffersCreateWithTransform({
    mutation: {
      onSuccess: async () => {
        await invalidateAfterApplicantChange(queryClient);
        options?.onSuccess?.();
      },
    },
  });
};

export const useCreateScreeningInterview = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useHireScreeningInterviewsCreateWithTransform({
    mutation: {
      onSuccess: async () => {
        await invalidateAfterApplicantChange(queryClient);
        options?.onSuccess?.();
      },
    },
  });
};

export const useRescheduleScreeningInterview = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useHireScreeningInterviewsPartialUpdateWithTransform({
    mutation: {
      onSuccess: async (_data, { id }) => {
        await Promise.all([
          invalidateAfterApplicantChange(queryClient),
          queryClient.invalidateQueries({ queryKey: getHireScreeningInterviewsRetrieveQueryKey(id) }),
        ]);
        options?.onSuccess?.();
      },
    },
  });
};

export const useCancelScreeningInterview = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  // Cancel has no date body, so we wrap the raw generated hook (no WithTz). mutate takes `{ id }`.
  return useHireScreeningInterviewsCancellationsCreate({
    mutation: {
      onSuccess: async () => {
        await invalidateAfterApplicantChange(queryClient);
        options?.onSuccess?.();
      },
    },
  });
};

// Records occurrence/outcome/note feedback for a past interview; `working` PATCHes the trial-shift
// endpoint, everything else the screening one (identical bodies). Optimism/rollback lives in the
// caller, so this just toasts on failure and reconciles on success.
export const useRecordInterviewFeedback = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  const onError = (error: unknown) => {
    captureException(error);
    toast.error("Couldn't save interview feedback. Please try again.");
  };

  // Don't gate the caller's handoff (offer dialog) or the chip spinner on the background refetch.
  const onSuccess = () => {
    void invalidateAfterApplicantChange(queryClient);
    options?.onSuccess?.();
  };

  const screening = useHireScreeningInterviewsFeedbackPartialUpdate({ mutation: { onError, onSuccess } });
  const working = useHireInterviewsFeedbackPartialUpdate({ mutation: { onError, onSuccess } });

  const mutateAsync = (variables: {
    id: string;
    type: NormalizedInterview["type"];
    data: PatchedScreeningInterviewFeedbackRequest;
  }) => {
    const { id, type, data } = variables;
    return type === "working" ? working.mutateAsync({ id, data }) : screening.mutateAsync({ id, data });
  };

  return { mutateAsync, isPending: screening.isPending || working.isPending };
};

export const useCancelJobOffer = (options?: { onSuccess?: () => void }) => {
  const queryClient = useQueryClient();

  return useHireJobOffersDestroy({
    mutation: {
      onSuccess: async () => {
        await invalidateAfterApplicantChange(queryClient);
        options?.onSuccess?.();
      },
    },
  });
};

export const useJobCreditStats = (options?: Options) =>
  useHireJobCreditsStatsRetrieve<JobCreditStats, Error>({ query: { enabled: options?.enabled } });

type CreateJobCreditInput = { organization_id: string; fee_cents: number; count: number };

export const useCreateJobCredits = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ organization_id, fee_cents, count }: CreateJobCreditInput) =>
      Promise.all(times(count, () => hireJobCreditsCreate({ organization_id, fee_cents }))),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: getHireJobCreditsStatsRetrieveQueryKey() }),
  });
};

type UseJobsForInviteOptions = { enabled?: boolean };

export const useJobsForInvite = (proId: number, options?: UseJobsForInviteOptions) => {
  const { data, isLoading, isError, isFetching } = useHireJobsProMatchStatusList(
    { pro_id: proId, status: "open" },
    { query: { enabled: options?.enabled } },
  );

  return { jobs: data ?? [], isLoading, isError, isFetching };
};

// Jobs for invite, enriched with this pro's existing offer status per posting.
export const useJobsWithOfferStatus = (proId: number, options?: UseJobsForInviteOptions) => {
  const { jobs: baseJobs, isLoading, isError, isFetching } = useJobsForInvite(proId, options);

  const { data: existingOffers } = useHireJobOffersList(
    { user_id: proId },
    { query: { enabled: options?.enabled, select: (data) => data } },
  );

  const jobs = (() => {
    if (!existingOffers?.length) return baseJobs;
    const offersByPosting = new Map(existingOffers.map((o: JobOffer) => [o.job_posting_id, o.status]));
    return baseJobs.map((job) => ({
      ...job,
      offer_status: offersByPosting.get(job.id) ?? null,
    }));
  })();

  return { jobs, isLoading, isError, isFetching };
};

// The group endpoint returns one row per (posting, member); collapse to one row per posting.
export const useJobsForInviteGroup = (groupId: number, options?: UseJobsForInviteOptions) => {
  const { data, isLoading } = useHireJobsProMatchStatusList(
    { group_id: groupId, status: "open" },
    { query: { enabled: options?.enabled } },
  );

  const jobs = (() => {
    // A job is offerable only if every member is eligible, so keep a blocked row over an eligible one.
    const isBlocked = (row: ProMatchStatusResult) =>
      Boolean(row.ineligibility_reason) || !canScheduleWorkingInterview(row.applied_stage);
    const byJob = new Map<string, ProMatchStatusResult>();
    for (const row of data ?? []) {
      const seen = byJob.get(row.id);
      if (!seen || (isBlocked(row) && !isBlocked(seen))) byJob.set(row.id, row);
    }
    return [...byJob.values()];
  })();

  return { jobs, isLoading };
};

/** Monday-first `{days,start,end}` rows the field speaks, from the wire's `days_of_week/start_time/end_time`. */
export const toAvailabilityWindows = (windows: Array<ManagerAvailabilityWindow>): Array<AvailabilityWindow> =>
  windows.map(({ days_of_week, start_time, end_time }) => ({
    days: days_of_week,
    // The field's selects are keyed on "HH:mm", so seconds have to go or the value matches no option.
    start: start_time.slice(0, 5),
    end: end_time.slice(0, 5),
  }));

const toWindowRequest = (windows: Array<AvailabilityWindow>): Array<ManagerAvailabilityWindowRequest> =>
  windows.map(({ days, start, end }) => ({ days_of_week: days, start_time: start, end_time: end }));

/**
 * How many of the manager's open postings this default governs, and how many carry a different week.
 *
 * The two counts are disjoint — a posting's week either equals this default or differs from it — so
 * they describe their sum in total, not a total and a subset of it. A posting pinned to exactly the
 * default's week counts as governed, which is why the API compares schedules rather than counting
 * postings that hold rows of their own.
 *
 * Invalidated by every mutation that can move the numbers: `invalidateAfterJobChange`, an override
 * save/clear, **and** a write to the default week — since the counts turn on what the default *says*,
 * editing it re-decides which postings match. That last one matters most: it is the Save button on the
 * card this footer sits under, so leaving it out would show a manager stale numbers about the very
 * edit they just made.
 */
export const useInterviewAvailabilityJobCounts = (enabled = true) =>
  useHireManagerAvailabilityWeekSummaryRetrieve({ query: { enabled } });

/** The manager's own account-level default. */
export const useInterviewAvailability = (enabled = true) =>
  useHireManagerAvailabilityWeekList({ query: { enabled, select: toAvailabilityWindows } });

/** Another manager's account-level default, by user id — for recommending against their availability. */
export const useManagerAvailabilityPreview = (userId: number | null | undefined, enabled = true) =>
  useHireManagerAvailabilityWeekPreviewList(
    { hiring_manager: userId ?? 0 },
    { query: { enabled: enabled && userId != null, select: toAvailabilityWindows } },
  );

// A generated query key is the request path, so a posting's key carries its id inline
// (`/api/hire/manager-availability/jobs/42/week/`) and there is no static prefix to pass as
// `queryKey`. Derived from the generator rather than written out, so a moved route still matches.
const JOB_ID_PLACEHOLDER = "{jobId}";
const [jobAvailabilityKeyTemplate] = getHireManagerAvailabilityJobsWeekRetrieveQueryKey(JOB_ID_PLACEHOLDER);
const jobAvailabilityKeyPrefix = jobAvailabilityKeyTemplate.slice(
  0,
  jobAvailabilityKeyTemplate.indexOf(JOB_ID_PLACEHOLDER),
);

export const useSaveInterviewAvailability = () => {
  const queryClient = useQueryClient();
  const mutation = useHireManagerAvailabilityWeekUpdate({
    mutation: {
      onSuccess: async () => {
        // Job-scoped reads inherit from this default, so they go stale on every write to it.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getHireManagerAvailabilityWeekListQueryKey() }),
          queryClient.invalidateQueries({
            predicate: ({ queryKey }) =>
              typeof queryKey[0] === "string" && queryKey[0].startsWith(jobAvailabilityKeyPrefix),
          }),
          // And so do the counts: they are decided by comparing each posting's week against this one,
          // so editing it moves postings between them. The footer renders directly under this Save.
          queryClient.invalidateQueries({ queryKey: getHireManagerAvailabilityWeekSummaryRetrieveQueryKey() }),
        ]);
      },
    },
  });
  return {
    ...mutation,
    // Resolves with the week the server kept, which is not always the week that was posted: touching
    // windows come back merged, and it sorts by start time. The caller needs both to tell those apart.
    mutateAsync: (windows: Array<AvailabilityWindow>) =>
      mutation.mutateAsync({ data: toWindowRequest(windows) }).then(toAvailabilityWindows),
  };
};

export const selectJobAvailability = (scope: { is_override: boolean; windows: Array<ManagerAvailabilityWindow> }) => ({
  isOverride: scope.is_override,
  windows: toAvailabilityWindows(scope.windows),
});

/** The windows in effect for a posting, and whether they are its own override. */
export const useJobInterviewAvailability = (jobId: string | undefined, enabled = true) =>
  useHireManagerAvailabilityJobsWeekRetrieve(jobId ?? "", {
    query: { enabled: enabled && !!jobId, select: selectJobAvailability },
  });

export const useSaveJobInterviewAvailability = () => {
  const queryClient = useQueryClient();
  const update = useHireManagerAvailabilityJobsWeekUpdate();
  const clear = useHireManagerAvailabilityJobsWeekDestroy();
  const invalidate = (jobPostingId: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: getHireManagerAvailabilityJobsWeekRetrieveQueryKey(jobPostingId) }),
      // Pinning or resetting a posting's week can move it between the two Settings counts — pinning it
      // to a *differing* week does, pinning it to the default's own week does not, and only the API
      // knows which. So invalidate either way rather than guessing here.
      queryClient.invalidateQueries({ queryKey: getHireManagerAvailabilityWeekSummaryRetrieveQueryKey() }),
    ]);

  return {
    /** Resolves with the week the server kept, which can hold fewer windows than were posted. */
    save: async (jobPostingId: string, windows: Array<AvailabilityWindow>) => {
      const scope = await update.mutateAsync({ jobPostingId, data: toWindowRequest(windows) });
      await invalidate(jobPostingId);
      return toAvailabilityWindows(scope.windows);
    },
    /** Drops the override so the posting inherits again; DELETE, not an empty PUT. */
    clearOverride: async (jobPostingId: string) => {
      await clear.mutateAsync({ jobPostingId });
      await invalidate(jobPostingId);
    },
  };
};
